import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { LuType } from "react-icons/lu";
import FontPickerGrid from "./FontPickerGrid";
import { SUPPORTED_GOOGLE_FONTS } from "../../../../common/font_extractor.es6";

// Menu geometry: keep in sync with _assessmentMenu.scss (.am-font-popover
// width) and _fontPickerGrid.scss (max-height + popover padding).
const MENU_W = 210;
const MENU_H = 340;
const GAP = 8;

// Dedicated font-family button: its own icon, its own dropdown — not buried
// inside a general "more options" gear popover. Opens a scrollable list of
// font names stacked one per row; hovering/focusing a row previews it live on
// the selected text, clicking commits it. Used by both the single-box pill
// (TopActionPill) and the multi-select bar (CanvasAlignToolbar), leftmost
// among the text-format buttons.
//
// The dropdown is PORTALED to <body> and opens BESIDE the TEXT BOX itself
// (anchorBoxId) — glued to the box's left edge, or its right edge when the
// canvas leaves no room on the left — never over it: the rows live-preview
// the font on that very box, so its text must stay visible. Without an
// anchorBoxId (multi-select bar) it falls back to "beside the toolbar".
export default function FontFamilyButton({
  label,
  value,
  onChange,
  onPreview,
  buttonClassName,
  anchorBoxId,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);
  const popRef = useRef(null);

  const close = () => {
    if (open && onPreview) onPreview(null);
    setOpen(false);
  };

  const place = () => {
    const btn = rootRef.current;
    if (!btn) return;
    const boxEl = anchorBoxId ? document.getElementById("box-" + anchorBoxId) : null;
    const anchor = boxEl || btn.closest(".am-pill") || btn.parentElement || btn;
    const r = anchor.getBoundingClientRect();
    const fitsLeft = r.left - MENU_W - GAP >= GAP;
    const left = fitsLeft ? r.left - MENU_W - GAP : r.right + GAP;
    const top = Math.max(GAP, Math.min(r.top, window.innerHeight - MENU_H - GAP));
    setPos({ left, top });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      const inBtn = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    // Follow the canvas on scroll/resize so the menu stays glued to the pill.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
    // Deliberately only re-subscribes on `open` — `close`/`place` are fresh
    // functions every render but always close over the latest props/refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="assessmentBoxMenu am-font-btn" ref={rootRef}>
      <button
        type="button"
        className={buttonClassName + (open ? " am-open" : "")}
        aria-label={label}
        title={label}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            close();
          } else {
            place();
            setOpen(true);
          }
        }}
      >
        <LuType />
      </button>
      {open && pos
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              className="assessmentBoxMenu am-popover am-font-popover"
              style={{ position: "fixed", left: pos.left, top: pos.top }}
              role="menu"
              aria-label={label}
            >
              <FontPickerGrid
                value={value}
                googleFonts={SUPPORTED_GOOGLE_FONTS}
                onPreview={onPreview}
                onSelect={(next) => {
                  onChange(next);
                  close();
                }}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

FontFamilyButton.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  buttonClassName: PropTypes.string.isRequired,
  anchorBoxId: PropTypes.string,
};
